import { useState, useEffect, useCallback } from 'react';
import { referralsService } from '../services/referrals';
import type {
  ReferralOverview,
  ReferralUserItem,
  ReferralUsersParams,
  InfluencerCodeItem,
  InfluencerCodesParams,
  ActivityItem,
  ActivityParams,
  PromoCodeItem,
  PromoCodesParams,
} from '../types/referrals';

// ============================================================================
// useReferralOverview
// ============================================================================

interface UseReferralOverviewReturn {
  data: ReferralOverview | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useReferralOverview(): UseReferralOverviewReturn {
  const [state, setState] = useState<{
    data: ReferralOverview | null;
    loading: boolean;
    error: string | null;
  }>({
    data: null,
    loading: true,
    error: null,
  });

  const fetch = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const data = await referralsService.getOverview();
      setState({ data, loading: false, error: null });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch referral overview';
      setState((prev) => ({ ...prev, loading: false, error: message }));
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { ...state, refetch: fetch };
}

// ============================================================================
// useReferralUsers — API returns { users: [...], total, limit, offset }
// ============================================================================

interface UseReferralUsersReturn {
  users: ReferralUserItem[];
  total: number;
  loading: boolean;
  error: string | null;
  page: number;
  totalPages: number;
  refetch: () => Promise<void>;
}

export function useReferralUsers(params: ReferralUsersParams): UseReferralUsersReturn {
  const limit = params.limit || 20;
  const offset = params.offset || 0;

  const [state, setState] = useState<{
    users: ReferralUserItem[];
    total: number;
    loading: boolean;
    error: string | null;
  }>({
    users: [],
    total: 0,
    loading: true,
    error: null,
  });

  const fetch = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const data = await referralsService.getUsers(params);
      setState({
        users: Array.isArray(data.users) ? data.users : [],
        total: data.total ?? 0,
        loading: false,
        error: null,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch referral users';
      setState((prev) => ({ ...prev, loading: false, error: message }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.search, params.hasReferrals, params.isInfluencer, params.sortBy, params.sortOrder, params.limit, params.offset]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const page = Math.floor(offset / limit) + 1;
  const totalPages = Math.max(1, Math.ceil(state.total / limit));

  return { ...state, page, totalPages, refetch: fetch };
}

// ============================================================================
// useInfluencerCodes — API returns { data: [...], total, limit, offset }
// ============================================================================

interface UseInfluencerCodesReturn {
  items: InfluencerCodeItem[];
  total: number;
  loading: boolean;
  error: string | null;
  page: number;
  totalPages: number;
  refetch: () => Promise<void>;
}

export function useInfluencerCodes(params: InfluencerCodesParams): UseInfluencerCodesReturn {
  const limit = params.limit || 20;
  const offset = params.offset || 0;

  const [state, setState] = useState<{
    items: InfluencerCodeItem[];
    total: number;
    loading: boolean;
    error: string | null;
  }>({
    items: [],
    total: 0,
    loading: true,
    error: null,
  });

  const fetch = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const data = await referralsService.getInfluencerCodes(params);
      setState({
        items: Array.isArray(data.data) ? data.data : [],
        total: data.total ?? 0,
        loading: false,
        error: null,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch influencer codes';
      setState((prev) => ({ ...prev, loading: false, error: message }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.search, params.status, params.sortBy, params.sortOrder, params.limit, params.offset]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const page = Math.floor(offset / limit) + 1;
  const totalPages = Math.max(1, Math.ceil(state.total / limit));

  return { ...state, page, totalPages, refetch: fetch };
}

// ============================================================================
// usePromoCodes — API returns { codes: [...], total, limit, offset }
// ============================================================================

interface UsePromoCodesReturn {
  items: PromoCodeItem[];
  total: number;
  loading: boolean;
  error: string | null;
  page: number;
  totalPages: number;
  refetch: () => Promise<void>;
}

export function usePromoCodes(params: PromoCodesParams): UsePromoCodesReturn {
  const limit = params.limit || 20;
  const offset = params.offset || 0;

  const [state, setState] = useState<{
    items: PromoCodeItem[];
    total: number;
    loading: boolean;
    error: string | null;
  }>({
    items: [],
    total: 0,
    loading: true,
    error: null,
  });

  const fetch = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const data = await referralsService.getPromoCodes(params);
      setState({
        items: Array.isArray(data.codes) ? data.codes : [],
        total: data.total ?? 0,
        loading: false,
        error: null,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch promo codes';
      setState((prev) => ({ ...prev, loading: false, error: message }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.isActive, params.campaignName, params.limit, params.offset]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const page = Math.floor(offset / limit) + 1;
  const totalPages = Math.max(1, Math.ceil(state.total / limit));

  return { ...state, page, totalPages, refetch: fetch };
}

// ============================================================================
// useReferralActivity — API returns { activities: [...], total, limit, offset }
// ============================================================================

interface UseReferralActivityReturn {
  items: ActivityItem[];
  total: number;
  loading: boolean;
  error: string | null;
  page: number;
  totalPages: number;
  refetch: () => Promise<void>;
}

export function useReferralActivity(params: ActivityParams): UseReferralActivityReturn {
  const limit = params.limit || 20;
  const offset = params.offset || 0;

  const [state, setState] = useState<{
    items: ActivityItem[];
    total: number;
    loading: boolean;
    error: string | null;
  }>({
    items: [],
    total: 0,
    loading: true,
    error: null,
  });

  const fetch = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const data = await referralsService.getActivity(params);
      setState({
        items: Array.isArray(data.activities) ? data.activities : [],
        total: data.total ?? 0,
        loading: false,
        error: null,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch referral activity';
      setState((prev) => ({ ...prev, loading: false, error: message }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.type, params.referralCode, params.limit, params.offset]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const page = Math.floor(offset / limit) + 1;
  const totalPages = Math.max(1, Math.ceil(state.total / limit));

  return { ...state, page, totalPages, refetch: fetch };
}
